import { ThemeEvents } from '@theme/events';

// Only the newsletter section uses this controller. The theme owns dialog UI,
// focus, animations, scroll locking, and the existing manual opening buttons.
if (!customElements.get('sys-newsletter')) {
  class AurlonaNewsletterAuto extends HTMLElement {
    async connectedCallback() {
      this.controller?.abort();
      this.cancelAutomatic();
      const controller = new AbortController();
      this.controller = controller;
      const { signal } = controller;

      try {
        this.configs = JSON.parse(this.getAttribute('configs'));
      } catch {
        return;
      }
      this.modal = this.querySelector('hdt-modal');
      this.dialog = this.querySelector('dialog');
      if (!this.configs?.id || !this.modal || !this.dialog) return;

      await customElements.whenDefined('hdt-modal');
      if (!this.isConnected || signal.aborted) return;

      if (window.Shopify?.designMode) {
        const section = this.closest('.shopify-section');
        section?.addEventListener('shopify:section:select', () => this.modal.open(), { signal });
        section?.addEventListener('shopify:section:deselect', () => this.modal.close(), { signal });
        return;
      }

      this.cookieName = `theme4:newsletter:${this.configs.id}`;
      this.opened = this.dialog.open;
      this.dialog.addEventListener(ThemeEvents.dialogOpening, () => {
        this.opened = true;
        this.cancelAutomatic();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        if (!this.opened) return;
        this.cancelAutomatic();
        this.rememberDismissal();
      }, { signal });

      if (this.opened || this.isDismissed()) return;
      this.automatic = new AbortController();

      if (this.configs.after === 'time') {
        this.timer = setTimeout(() => this.showAutomatically(), this.numberSetting('time_delay', 8) * 1000);
      } else if (this.configs.after === 'scroll') {
        const checkScroll = () => {
          if (window.scrollY <= this.numberSetting('scroll_delay', 1100)) return;
          this.automatic?.abort();
          this.showAutomatically();
        };
        window.addEventListener('scroll', checkScroll, { passive: true, signal: this.automatic.signal });
        checkScroll();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.cancelAutomatic();
    }

    numberSetting(name, fallback) {
      const value = Number(this.configs[name]);
      return Number.isFinite(value) ? Math.max(0, value) : fallback;
    }

    cancelAutomatic() {
      clearTimeout(this.timer);
      this.timer = undefined;
      this.automatic?.abort();
    }

    isDismissed() {
      try {
        return document.cookie.split(';').some(cookie => cookie.trim() === `${this.cookieName}=shown`);
      } catch {
        return false;
      }
    }

    rememberDismissal() {
      const seconds = Math.round(this.numberSetting('day_next', 3) * 86400);
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      try {
        document.cookie = `${this.cookieName}=shown; Max-Age=${seconds}; Path=/; SameSite=Lax${secure}`;
      } catch {
        // Storage restrictions must not break closing or manual reopening.
      }
    }

    showAutomatically() {
      if (!this.isConnected || this.controller.signal.aborted || this.opened || this.isDismissed()) {
        this.cancelAutomatic();
        return;
      }
      // Let an active cart, search, or other dialog finish before showing this one.
      if (document.visibilityState === 'hidden' || document.querySelector('dialog[open]')) {
        this.timer = setTimeout(() => this.showAutomatically(), 1000);
        return;
      }
      this.cancelAutomatic();
      this.modal.open();
    }
  }

  customElements.define('sys-newsletter', AurlonaNewsletterAuto);
}
